// @flow

type FieldType =
  | 'text'
  | 'number'
  | 'checkbox'
  | 'radio'
  | 'select'
  | 'color'
  | 'textarea'
  | 'label';

export type Field = {
  //The property name to pass to the image gallery widget
  propertyName: string,
  //The field type
  type: FieldType,
  //The field name in gallery internals
  enum?: string,
  //The field label translation key
  label?: string,
  //boolean to disable the field
  disabled?: boolean,
  //clearable
  clearable?: boolean,
  //function name to determine enable/disable based on state
  enabled?: Function,
  //The field tooltip text translation key
  tooltip?: string,
  //Array of fields that the field depend on to be visible
  dependOnFields?: Array<{ name: string, value: string | number | boolean }>,
  //The parent group e.g zoomProps
  group?: string,
  //Field set help text
  fieldSetHelp?: string,
  //Max value for number field
  max?: number,
  //Min value for number field (default is 0)
  min?: number,
  //Step value for number field (default is 1)
  step?: number,
  //Define if the options field is multiple
  multiple?: boolean,
  //The array of the options
  options?: Array<SelectOption | string>,
  //The enum name.
  enum?: string,
  //Toggle "on" label
  onValue?: string,
  //Toggle "off" label
  offValue?: string,
  //input props
  inputProps?: Object,
};

export type SelectOption = {
  //The select option value
  value: string | number | Object,
  //The select option label
  label?: string,
  //The select option icon
  icon?: string,
  //The select option key
  key?: string,
  //The select option cls (for icon if needed)
  cls?: string,
};

export type FieldSet = {
  //The field-set title translation key
  fieldSetTitle: string,
  //Field set help text
  fieldSetHelp?: string,
  //Array of fields
  fields: Array<Field>,
  //hide the label
  hideTitle?: boolean,
  //tooltip info
  tooltip?: string,
  //tooltip learn-more link
  fieldSetUrl?: string,
};

const FORM_CONFIG: Array<FieldSet> = [
  {
    fieldSetTitle: 'widgetParametersTitle',
    fieldSetUrl: 'widget_parameters',
    tooltip: 'learnMore',
    fields: [
      {
        label: 'layoutLabel',
        type: 'radio',
        propertyName: 'layout',
        options: [
          {
            value: { mode: 'classic' },
            label: 'Classic',
            icon: 'Icons/layout_classic',
            key: 'classic1',
          },
          {
            value: { mode: 'expanded', columns: 1 },
            label: '1 column',
            subLabel: 'Expanded',
            icon: 'Icons/layout_1-column',
            key: 'expanded1',
          },
          {
            value: { mode: 'expanded', columns: 2 },
            label: '2 column',
            subLabel: 'Expanded',
            icon: 'Icons/layout_2-column',
            key: 'expanded2',
          },
          {
            value: { mode: 'expanded', columns: 3 },
            label: '3 column',
            subLabel: 'Expanded',
            icon: 'Icons/layout_3-column',
            key: 'expanded3',
          },
        ],
      },
      {
        group: 'themeProps',
        label: 'paletteLabel',
        type: 'label',
        propertyName: 'paletteLabel',
      },
      {
        group: 'themeProps',
        label: 'activeLabel',
        type: 'color',
        propertyName: 'active',
        tooltip: 'active'
      },
      {
        group: 'themeProps',
        label: 'onPrimaryLabel',
        type: 'color',
        propertyName: 'onPrimary',
        tooltip: 'onPrimary'
      },
      {
        group: 'themeProps',
        label: 'primaryLabel',
        type: 'color',
        propertyName: 'primary',
        tooltip: 'primary'
      },
      {
        label: 'transitionLabel',
        type: 'select',
        propertyName: 'transition',
        options: ['none', 'fade', 'slide'],
        clearable: false,
        enabled: (formState) => formState.layout == 'classic1',
      },
    ],
  },
  {
    fieldSetTitle: 'mainViewerTitle',
    fieldSetUrl: 'main_viewer_parameters',
    tooltip: 'learnMore',
    fields: [
      {
        label: 'aspectRatioLabel',
        type: 'radio',
        propertyName: 'aspectRatio',
        options: [
          { value: 'square', label: 'square', icon: 'Icons/square' },
          { value: '16:9', label: '16:9', icon: 'Icons/ratio-16-9' },
          { value: '9:16', label: '9:16', icon: 'Icons/rectangle' },
          { value: 'more', label: 'more', icon: 'Icons/more' },
        ],
      },
      {
        clearable: false,
        label: 'aspectRatioMoreLabel',
        type: 'select',
        propertyName: 'moreAspectRatio',
        options: [
          '1:1',
          '3:4',
          '4:6',
          '5:7',
          '5:8',
          '4:3',
          '6:4',
          '7:5',
          '8:5',
        ],
        icons: {
          '1:1': 'Icons/aspect-ratio-square',
          '3:4': 'Icons/aspect-ratio-9-16',
          '4:6': 'Icons/aspect-ratio-portrait',
          '5:7': 'Icons/aspect-ratio-portrait',
          '5:8': 'Icons/aspect-ratio-9-16',
          '4:3': 'Icons/aspect-ratio-4-3',
          '6:4': 'Icons/aspect-ratio-16-9',
          '7:5': 'Icons/aspect-ratio-4-3',
          '8:5': 'Icons/aspect-ratio-16-9',
        },
        dependOnFields: [{ name: 'aspectRatio', value: 'more' }],
      },
      {
        clearable: false,
        label: 'navigationLabel',
        type: 'select',
        propertyName: 'navigation',
        enum: 'Navigation',
        enabled: (formState) => formState.layout == 'classic1',
      },
      {
        group: 'navigationButtonProps',
        label: 'thumbnailNavigationLabel',
        type: 'radio',
        propertyName: 'shape',
        options: [
          { value: 'rectangle', label: 'rectangle', icon: 'Icons/rectangle' },
          { value: 'square', label: 'square', icon: 'Icons/square' },
          { value: 'round', label: 'round', icon: 'Icons/round' },
          { value: 'radius', label: 'radius', icon: 'Icons/radius' },
          { value: 'none', label: 'none', icon: 'Icons/none' },
        ],
        dependOnFields: [{ name: 'navigation', value: 'always,mouseover' }]
      },
      {
        group: 'navigationButtonProps',
        label: 'navigationColorLabel',
        type: 'label',
        propertyName: 'navigationColorLabel',
        dependOnFields: [{ name: 'navigation', value: 'always,mouseover' }]
      },
      {
        group: 'navigationButtonProps',
        label: 'iconColorLabel',
        type: 'color',
        propertyName: 'iconColor',
        dependOnFields: [{ name: 'navigation', value: 'always,mouseover' }]
      },
      {
        group: 'navigationButtonProps',
        label: 'iconBackgroundLabel',
        type: 'color',
        propertyName: 'color',
        dependOnFields: [{ name: 'navigation', value: 'always,mouseover' }]
      },
      {
        label: 'zoomLabel',
        type: 'checkbox',
        propertyName: 'zoom',
      },
      {
        clearable: false,
        group: 'zoomProps',
        label: 'zoomTypeLabel',
        type: 'select',
        propertyName: 'type',
        enum: 'ZoomType',
        dependOnFields: [{ name: 'zoom', value: true }],
      },
      {
        group: 'zoomProps',
        label: 'zoomStepsLabel',
        type: 'number',
        propertyName: 'steps',
        dependOnFields: [
          { name: 'zoom', value: true },
          { name: 'zoomProps.type', value: 'popup' },
        ],
        min: 0,
        max: 5,
        step: 1,
      },
      {
        group: 'zoomProps',
        label: 'zoomLevelLabel',
        type: 'number',
        propertyName: 'level',
        dependOnFields: [
          { name: 'zoom', value: true }
        ],
        enabled: (formState) =>
          formState.zoomProps.type !== 'popup'
          || formState.zoomProps.type == 'popup' && formState.zoomProps.steps > 0,
        min: 1,
        max: 3,
        step: 0.1,
      },
      {
        clearable: false,
        group: 'zoomProps',
        label: 'viewerPositionLabel',
        type: 'select',
        propertyName: 'viewerPosition',
        enum: 'ZoomViewerPosition',
        dependOnFields: [
          { name: 'zoom', value: true },
          { name: 'zoomProps.type', value: 'flyout' },
        ],
      },
      {
        clearable: false,
        group: 'zoomProps',
        label: 'zoomTriggerLabel',
        type: 'select',
        propertyName: 'trigger',
        enum: 'ZoomTrigger',
        enabled: (formState) => formState.zoomProps.type !== 'popup',
        dependOnFields: [{ name: 'zoom', value: true }],
      },
    ],
  },
  {
    fieldSetTitle: 'carouselTitle',
    fieldSetUrl: 'carousel_parameters',
    tooltip: 'learnMore',
    fields: [
      {
        label: 'carouselStyleLabel',
        type: 'radio',
        propertyName: 'carouselStyle',
        icons: {
          thumbnails: 'Icons/carousel-thumbnails',
          indicators: 'Icons/carousel-indicators',
          none: 'Icons/carousel-none'
        },
        enum: 'CarouselStyle',
        enabled: (formState) =>
          formState.layout == 'classic1' || formState.layout == 'expanded1',
      },
      {
        clearable: false,
        label: 'carouselLocationLabel',
        type: 'select',
        propertyName: 'carouselLocation',
        enum: 'CarouselLocation',
        enabled: (formState) =>
          formState.layout == 'classic1' || formState.layout == 'expanded1',
        dependOnFields: [{ name: 'carouselStyle', value: ('thumbnails,indicators') }]
      },
      {
        group: 'thumbnailProps',
        label: 'widthLabel',
        type: 'number',
        propertyName: 'width',
        dependOnFields: [{ name: 'carouselStyle', value: 'thumbnails' }],
        min: 50,
        max: 300,
        step: 10,
      },
      {
        group: 'thumbnailProps',
        label: 'heightLabel',
        type: 'number',
        propertyName: 'height',
        dependOnFields: [{ name: 'carouselStyle', value: 'thumbnails' }],
        min: 50,
        max: 300,
        step: 10,
      },
      {
        label: 'carouselOffsetLabel',
        type: 'number',
        propertyName: 'carouselOffset',
        max: 100,
        step: 10,
        dependOnFields: [{ name: 'carouselStyle', value: ('thumbnails,indicators') }]
      },
      {
        group: 'thumbnailProps',
        label: 'carouselButtonShape',
        type: 'radio',
        propertyName: 'navigationShape',
        enum: 'ButtonShape',
        enabled: (formState) =>
          formState.layout == 'classic1' || formState.layout == 'expanded1',
        dependOnFields: [{ name: 'carouselStyle', value: 'thumbnails' }],
      },
      {
        group: 'thumbnailProps',
        label: 'carouselButtonColor',
        type: 'label',
        propertyName: 'navigationColorLabel',
        dependOnFields: [
          { name: 'carouselStyle', value: 'thumbnails' },
          { name: 'thumbnailProps.navigationShape', value: 'round,square,radius,rectangle' }
        ]
      },
      {
        group: 'thumbnailProps',
        label: 'iconColorLabel',
        type: 'color',
        propertyName: 'navigationIconColor',
        dependOnFields: [
          { name: 'carouselStyle', value: 'thumbnails' },
          { name: 'thumbnailProps.navigationShape', value: 'round,square,radius,rectangle' }
        ]
      },
      {
        group: 'thumbnailProps',
        label: 'iconBackgroundLabel',
        type: 'color',
        propertyName: 'navigationColor',
        dependOnFields: [
          { name: 'carouselStyle', value: 'thumbnails' },
          { name: 'thumbnailProps.navigationShape', value: 'round,square,radius,rectangle' }
        ]
      },
      {
        propertyName: 'labelcarouselStyle',
        type: 'label',
        dependOnFields: [{ name: 'carouselStyle', value: 'thumbnails' }],
      },
      {
        clearable: false,
        group: 'thumbnailProps',
        label: 'selectedStyleLabel',
        type: 'select',
        propertyName: 'selectedStyle',
        enum: 'SelectedStyles',
        enabled: (formState) =>
          formState.layout == 'classic1' || formState.layout == 'expanded1',
        dependOnFields: [{ name: 'carouselStyle', value: 'thumbnails' }],
      },
      //Only if selected
      {
        group: 'thumbnailProps',
        label: 'selectedBorderPositionLabel',
        type: 'select',
        clearable: false,
        propertyName: 'selectedBorderPosition',
        dependOnFields: [
          { name: 'thumbnailProps.selectedStyle', value: 'border,all' },
          { name: 'carouselStyle', value: 'thumbnails' },
        ],
        enum: 'SelectedBorderPosition',
      },
      {
        group: 'thumbnailProps',
        label: 'selectedBorderWidthLabel',
        type: 'number',
        propertyName: 'selectedBorderWidth',
        dependOnFields: [
          { name: 'thumbnailProps.selectedStyle', value: 'border,all' },
          { name: 'carouselStyle', value: 'thumbnails' },
        ],
        min: 0,
        max: 10,
      },
      //Media icon
      {
        dependOnFields: [{ name: 'carouselStyle', value: 'thumbnails' }],
        group: 'thumbnailProps',
        label: 'mediaSymbolBgShapeLabel',
        type: 'radio',
        propertyName: 'mediaSymbolShape',
        enum: 'MediaSymbolShape',
      },

      {
        group: 'indicatorProps',
        label: 'indicatorShapeLabel',
        type: 'radio',
        propertyName: 'shape',
        enum: 'IndicatorShape',
        enabled: (formState) =>
          formState.layout == 'classic1' || formState.layout == 'expanded1',
        dependOnFields: [{ name: 'carouselStyle', value: 'indicators' }],
      },
    ],
  },
];

export default FORM_CONFIG;
